"use client";

import { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { usePathname } from "next/navigation";
import { OspToast } from "@ospweb/sdk-ui";
import { rootStore } from "@store/rootStore";
import { useMemberUser } from "@hooks/useMemberUser";
import { usePlanGoalChatOpen } from "@hooks/plan/usePlanGoalChatOpen";
import { useIsPc } from "@hooks/useIsPc";
import RetryTranslations from "@translate/screens/plan/constants/planDashboardTokens";
import { I18nUtil } from "@utils/i18n";
import { openOnboardingPaywall } from "@components/ChatOnboarding/openOnboardingPaywall";
import { clearCheckoutStash } from "@utils/member/checkoutSessionStash";
import { channelRequiresReturnConfirm } from "@utils/member/checkoutChannel";
import { Track } from "@/src/lib/tracking";
import type { Member } from "@features/api/memberApi";
import {
  buildConfirmPendingMessage,
  confirmCheckoutWithPolling,
} from "@hooks/useCheckoutReturnConfirm";
import { resumeOnboardingCheckout } from "./resumeOnboardingCheckout";
import {
  consumeOnboardingCheckout,
  hasEffectiveMembership,
  ONBOARDING_CHECKOUT_QUERY,
  pollOnboardingMembership,
  readOnboardingCheckout,
  saveOnboardingCheckout,
} from "./onboardingCheckoutContinuation";

const RetryI18n = I18nUtil.createScoped(RetryTranslations);

/** 查一次会员态；刷新失败按未生效处理（付费墙重开后仍可重试） */
async function hasMembershipNow(
  refresh: () => Promise<{ data?: Member }>,
): Promise<boolean> {
  try {
    return hasEffectiveMembership((await refresh()).data);
  } catch {
    return false;
  }
}

/**
 * onboarding 首付费墙 web 下单的回跳续接（全局挂载于 LoginModalProvider）。
 *
 * @returns isConfirming —— AIRWALLEX 回跳主动确认期间为 true，挂载方据此渲染确认遮罩（EDG-552，F4）
 */
export function useOnboardingCheckoutReturn(): { isConfirming: boolean } {
  const [isConfirming, setIsConfirming] = useState(false);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id ?? "";
  const pathname = usePathname();
  const { refetch } = useMemberUser();
  const { openPlanResumeDraftGoalChat } = usePlanGoalChatOpen();
  const [isPC] = useIsPc();
  const handled = useRef<{ id: string; cancelled: boolean } | undefined>(
    undefined,
  );
  const latest = useRef({ refetch, openPlanResumeDraftGoalChat, isPC });
  latest.current = { refetch, openPlanResumeDraftGoalChat, isPC };

  useEffect(() => {
    let activeRun: { id: string; cancelled: boolean } | undefined;
    const restore = async () => {
      if (!ownerId) {
        return;
      }
      const url = new URL(window.location.href);
      const callbackId = url.searchParams.get(ONBOARDING_CHECKOUT_QUERY);
      const pending = readOnboardingCheckout(ownerId);
      const clearCallback = () => {
        for (const key of [
          ONBOARDING_CHECKOUT_QUERY,
          "payment",
          "planId",
          "channel",
        ]) {
          url.searchParams.delete(key);
        }
        window.history.replaceState(
          window.history.state,
          "",
          `${url.pathname}${url.search}${url.hash}`,
        );
      };
      if (callbackId !== null && (!pending || callbackId !== pending.id)) {
        activeRun = { id: callbackId, cancelled: false };
        const run = activeRun;
        try {
          await latest.current.refetch();
        } catch {
          // The callback cannot recover an order or draft without its matching context.
        }
        if (
          !run.cancelled &&
          rootStore.user.ownerInfo?.id === ownerId &&
          new URL(window.location.href).searchParams.get(
            ONBOARDING_CHECKOUT_QUERY,
          ) === callbackId
        ) {
          clearCallback();
          OspToast.info({ content: RetryI18n.t("Please try again later.") });
        }
        return;
      }
      if (
        !pending ||
        (handled.current?.id === pending.id && !handled.current.cancelled)
      ) {
        return;
      }
      const onOriginalPage =
        `${url.pathname}${url.search}` === pending.returnPath;
      if (callbackId !== pending.id && !onOriginalPage) {
        return;
      }
      activeRun = { id: pending.id, cancelled: false };
      const run = activeRun;
      handled.current = run;
      const isCurrent = () =>
        !run.cancelled &&
        rootStore.user.ownerInfo?.id === ownerId &&
        readOnboardingCheckout(ownerId)?.id === pending.id;
      const success =
        callbackId === pending.id &&
        url.searchParams.get("payment") === "success" &&
        url.searchParams.get("planId") === pending.product.id;
      const productId = pending.product.id ?? "";
      if (
        callbackId === pending.id &&
        url.searchParams.get("payment") === "cancel"
      ) {
        // 与定价页取消回跳同口径（AC-17）：清暂存并记一笔
        clearCheckoutStash("checkout", productId);
        Track.event("checkout_result", {
          channel: pending.channel,
          product_id: productId,
          result: "cancelled",
        });
      }
      if (callbackId === pending.id) {
        clearCallback();
        pending.returnPath = `${url.pathname}${url.search}`;
        saveOnboardingCheckout(pending);
      }
      const resume = () => {
        if (!isCurrent()) {
          return;
        }
        resumeOnboardingCheckout(
          ownerId,
          pending.id,
          latest.current.openPlanResumeDraftGoalChat,
        );
      };
      if (pending.dismissedAt !== undefined && callbackId !== pending.id) {
        // 用户关过待生效付费墙：不再自动弹窗 / 遮罩 / 提示（否则 2 小时内每次刷新都会先等约 5 秒再弹墙），
        // 只静默查一次会员态 —— 已生效（webhook 晚到 / 后端补建）就续接 draft goal，未生效什么都不做
        if (await hasMembershipNow(() => latest.current.refetch())) {
          resume();
        }
        return;
      }
      const mustConfirm = success || pending.phase === "pending";
      if (mustConfirm) {
        saveOnboardingCheckout({ ...pending, phase: "pending" });
      }
      let active = false;
      let confirmTimedOut = false;
      if (success && channelRequiresReturnConfirm(pending.channel)) {
        // 需主动确认的渠道（AIRWALLEX）：先用暂存 session_id 主动确认；确认成功即可恢复，无需再轮询会员态
        setIsConfirming(true);
        try {
          const outcome = await confirmCheckoutWithPolling({
            planId: productId,
            channel: pending.channel,
          });
          active = outcome.result === "confirmed";
          confirmTimedOut = outcome.result === "pending_timeout";
          if (outcome.result === "no_session") {
            active = await pollOnboardingMembership(
              () => latest.current.refetch(),
              undefined,
              isCurrent,
            );
          }
        } finally {
          setIsConfirming(false);
        }
        if (!active) {
          // 主动确认没拿到结果（超时 / 失败）不代表没生效：webhook 可能已开通会员（beta 2026-10-13 实测，
          // 左下角已是 Complete 但付费墙仍重开）。重开付费墙前以会员态为准再查一次
          active = await hasMembershipNow(() => latest.current.refetch());
        }
      } else if (mustConfirm) {
        active = await pollOnboardingMembership(
          () => latest.current.refetch(),
          undefined,
          isCurrent,
        );
      } else {
        active = await hasMembershipNow(() => latest.current.refetch());
      }
      if (!isCurrent() || readOnboardingCheckout(ownerId)?.id !== pending.id) {
        return;
      }
      if (active) {
        resume();
        return;
      }
      if (confirmTimedOut) {
        // 已轮询 confirm ≈12 秒且会员态仍未生效：只提示「生效中」，从不报失败（AC-15）；
        // 付费墙以 checkoutPending 重开，挂载后自动轮询会员态，生效即关闭（用户也可点「Check payment status」）
        OspToast.info({ content: buildConfirmPendingMessage() });
      } else if (mustConfirm) {
        OspToast.info({ content: RetryI18n.t("Please try again later.") });
      }
      let redirected = false;
      const outcome = await openOnboardingPaywall(latest.current.isPC, {
        onboardingFirstWall: true,
        restoredPlanId: pending.selectedPlanId,
        checkoutPending: mustConfirm,
        analyticsSource: pending.analyticsSource,
        onWebCheckoutRedirect: () => {
          redirected = true;
        },
      });
      if (redirected || !isCurrent()) {
        return;
      }
      if (outcome === "purchased") {
        // This result is emitted only after the wall's membership refresh (or native confirmation).
        resume();
        return;
      }
      if (mustConfirm) {
        // 记录保留以便付款生效后续接，但标记已关闭，之后回到页面不再自动弹墙（Q8）
        const latestRecord = readOnboardingCheckout(ownerId);
        if (latestRecord?.id === pending.id) {
          saveOnboardingCheckout({ ...latestRecord, dismissedAt: Date.now() });
        }
        run.cancelled = true;
        return;
      }
      // V3.1.3（EDG-585 · D16）：关闭后不再直接拉起引导；一屏引导由首墙预约 + tab 兑现
      consumeOnboardingCheckout(ownerId, pending.id);
    };
    void restore();
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void restore();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      if (activeRun) {
        activeRun.cancelled = true;
      }
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [ownerId, pathname]);

  return { isConfirming };
}
